import {useMemo} from 'react';

import {Box, Typography} from '@mui/material';

import {DocumentUploadedPreview} from 'src/components/DocumentUploadedPreview';
import {TabSelectCard} from 'src/components/TabSelectCard';
import {useAddMerchantContext} from 'src/contexts/AddMerchantProvider';

import {Principals} from './components/Principals';
import {SupportingDocumentsView} from './components/ProofOfBank';

type SupportingDocument = {
    url: string;
    fileName: string;
    size: string;
    fileType: string;
};

export function createSupportingDocument(
    file: { locationUrl: string; fileName?: string; fileType?: string; fileSize?: number },
    defaultType: string
): SupportingDocument {
    return {
        url: file.locationUrl,
        fileName: file.fileName || file.fileType || defaultType,
        size: file.fileSize ? `${file.fileSize} KB` : '',
        fileType: file.fileType || defaultType,
    };
}

export const PrincipleSection: React.FC = () => {
    const {merchantApplication} = useAddMerchantContext();
    const data = useMemo(() => {
        const principalDocuments =
            merchantApplication?.supportingDocuments?.principalDocuments ?? [];
        const principals = principalDocuments
            .filter((doc, index) => {
                const principalInfo = merchantApplication?.merchantInformation?.principals?.[index];

                return principalInfo?.isBeneficialOwner;
            })
            .map(({firstName, lastName, addressFile, idFile}) => {
                return {
                    firstName,
                    lastName,
                    personalDocuments: [
                        {
                            fileName:
                                addressFile?.fileName ||
                                addressFile?.fileType ||
                                'Proof Of Address',
                            url: addressFile?.locationUrl,
                            size: `${addressFile?.fileSize} KB`,
                        },
                        {
                            fileName:
                                idFile?.settlementAccountFile || idFile?.fileType || 'Proof Of Id',
                            url: idFile?.locationUrl,
                            size: `${idFile?.fileSize} KB`,
                        },
                    ],
                };
            });

        const settlementAccountFile =
            merchantApplication?.supportingDocuments?.proofOfBank?.settlementAccountFile;
        const chargeableAccountFile =
            merchantApplication?.supportingDocuments?.proofOfBank?.chargeableAccountFile;
        const outletFiles =
            merchantApplication?.supportingDocuments?.proofOfBank?.outletFiles ?? [];

        const proofOfBank = {
            settlementBank: {
                url: settlementAccountFile?.locationUrl,
                fileName: settlementAccountFile?.fileName || settlementAccountFile?.fileType,
                size: settlementAccountFile?.fileSize
                    ? `${settlementAccountFile?.fileSize} KB`
                    : '',
            },
            chargeableBank: {
                url: chargeableAccountFile?.locationUrl,
                fileName: chargeableAccountFile?.fileName || chargeableAccountFile?.fileType,
                size: chargeableAccountFile?.fileSize
                    ? `${chargeableAccountFile?.fileSize} KB`
                    : '',
            },
            outlet2: {
                url: outletFiles?.map(({locationUrl}) => {
                    return locationUrl;
                }),
                fileName:
                    outletFiles?.map(({fileName}) => {
                        return fileName;
                    }) ||
                    outletFiles?.map(({fileType}) => {
                        return fileType;
                    }),
                size: outletFiles.map(({fileSize}) => {
                    return `${fileSize} KB`;
                }),
            },
        };

        const supportingDocuments: SupportingDocument[] = [];

        if (merchantApplication?.supportingDocuments?.partnershipDeedFile) {
            supportingDocuments.push(
                createSupportingDocument(
                    merchantApplication.supportingDocuments.partnershipDeedFile,
                    'Partnership Deed'
                )
            );
        }

        if (merchantApplication?.supportingDocuments?.businessReportFile) {
            supportingDocuments.push(
                createSupportingDocument(
                    merchantApplication.supportingDocuments.businessReportFile,
                    'Business Report'
                )
            );
        }

        if (merchantApplication?.supportingDocuments?.b1FormFile) {
            supportingDocuments.push(
                createSupportingDocument(
                    merchantApplication.supportingDocuments.b1FormFile,
                    'B1 Form'
                )
            );
        }

        if (merchantApplication?.supportingDocuments?.otherFile) {
            supportingDocuments.push(
                createSupportingDocument(merchantApplication.supportingDocuments.otherFile, 'Other')
            );
        }

        return {principals, proofOfBank, supportingDocuments};
    }, [merchantApplication]);

    const formattedOutlet2Data = {
        fileName: data.proofOfBank.outlet2.fileName,
        url: data.proofOfBank.outlet2.url,
        size: data.proofOfBank.outlet2.size,
    };

    const TABS = useMemo(() => {
        const dataOfOutlet = formattedOutlet2Data;

        const mappedData = dataOfOutlet?.fileName?.map((fileName, index) => {
            return {
                label: `Outlet ${index + 1}`,
                file: {
                    fileName,
                    url: dataOfOutlet.url[index],
                    size: dataOfOutlet.size[index],
                },
            };
        });

        return [
            {
                header: 'Principals',
                children: <Principals principals={data.principals} />,
            },
            {
                header: 'Proof of Bank(s)',
                children: (
                    <SupportingDocumentsView
                        heading="Proof of Bank(s)"
                        documents={[
                            {
                                label: 'Settlement Bank',
                                file: data.proofOfBank.settlementBank,
                            },
                            {
                                label: 'Chargeable Bank',
                                file: data.proofOfBank.chargeableBank,
                            },
                            ...mappedData,
                        ]}
                    />
                ),
            },
            {
                header: 'Supporting Documents',
                children: (
                    <Box display="flex" flexDirection="row" gap="20px">
                        {data.supportingDocuments.length ? (
                            data.supportingDocuments.map(
                                (document: SupportingDocument, index: number) => {
                                    return document.url ? (
                                        <DocumentUploadedPreview
                                            key={index}
                                            file={document}
                                            label={document?.fileType}
                                        />
                                    ) : null;
                                }
                            )
                        ) : (
                            <Typography>No Documents have been uploaded</Typography>
                        )}
                    </Box>
                ),
            },
        ];
    }, [data]);

    return <TabSelectCard tabs={TABS} />;
};
